import React, { useContext, useEffect, useRef, useState } from 'react';
import { TABTestItem } from '../types/ab';
import TableCell from 'Components/Common/components/TableCell';
import { useHistory } from 'react-router-dom';
import { capitalizeFirstLetter, compareUsernames, convertToDateMonthYear } from '../utils/ABHelper';
import { DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import CustomChip from './CustomChip';
import OverflowOption from './OverflowOption'
import { COMPLETED_STATUS, DELETE, DRAFT_STATUS, EDIT, SCHEDULED_STATUS } from '../utils/Constants';
import { ABContext } from '../contexts/ABContext';
import ResultsList from './ResultsList';
import AuthManager from 'Common/AuthManager';
import { toast } from 'react-toastify';

type TABTableProps = {
    abTests: TABTestItem[];
    setDeleteTest: React.Dispatch<React.SetStateAction<boolean>>;
    setDeleteItem: React.Dispatch<React.SetStateAction<{ id: string, name: string }>>;
    deletePermission: boolean;
    updatePermission: boolean;
}

const ABTable = ({ abTests, setDeleteTest, setDeleteItem, deletePermission, updatePermission }: TABTableProps) => {
    const history = useHistory();
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [showIndex, setShowIndex] = useState<number | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const abContext = useContext(ABContext);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowIndex(null);
            }
        }
        function handleScroll() {
            if (showIndex !== null) {
                setShowIndex(null);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [showIndex]);

    const onDelete = (e: MouseEvent, row: TABTestItem) => {
        e.stopPropagation();
        setShowIndex(null);
        if (!deletePermission) { history.push("/workspace/module/permission/denied"); return; }

        if (!compareUsernames(row?.ownership?.username, getUserName())) {
            toast.error("Deletion restricted to test owner");
            return;
        }
        setDeleteItem({ id: row?.id, name: row?.name }); setDeleteTest(true);
    }
    const onEdit = (e: MouseEvent, row: TABTestItem) => {
        e.stopPropagation();
        setShowIndex(null);
        if (!updatePermission) { history.push("/workspace/module/permission/denied"); return; }
        if (!compareUsernames(row?.ownership?.username, getUserName())) {
            toast.error("Editing restricted to test owner");
            return;
        }
        navigateToDetailPage(row.name, row.id);
    }

    const handleOverflowClick = (event: React.MouseEvent<HTMLDivElement>, rowIndex: number) => {
        setShowIndex(rowIndex); event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({
            top: rect.bottom - window.scrollY,
            left: rect.left - window.scrollX,
        });
    }

    const navigateToDetailPage = (name: string, testId: string = '') => {
        abContext.clearData();
        history.push(`/gms/ab/detail/${testId}`);
    }
    const navigateToResultPage = (name: string, testId: string = '') => {
        abContext.clearData();
        history.push(`/gms/ab/result/${testId}`);
    }

    const getUserName = () => {
        let userObj = null;
        if (AuthManager.isAuthenticated()) userObj = AuthManager.getAuthUser();
        if (!userObj?.profile) return capitalizeFirstLetter("guest");
        return capitalizeFirstLetter(userObj.profile.first_name ?? '');
    };

    const getUserId = () => {
        const userobj = AuthManager.getAuthUser();
        const userProfile = userobj?.profile;
        const userid = userProfile?.id;
        return userid
    };

    const hasEditPermission = (row: TABTestItem) => [SCHEDULED_STATUS, DRAFT_STATUS].includes(row?.status) && row?.ownership?.userid === getUserId() && updatePermission;
    const hasDeletePermission = (row: TABTestItem) => [SCHEDULED_STATUS, DRAFT_STATUS, COMPLETED_STATUS].includes(row?.status) && row?.ownership?.userid === getUserId() && deletePermission;

    return (
        <div id="ab-test-table-container" className="tw-border tw-border-solid tw-rounded tw-mt-3 tw-border-[#DFE0E6] tw-border-r-2">
            <div id="ab-test-scroll-container" className="table-container tw-rounded" style={{ width: '100%', height: '600px', overflowX: 'auto', position: 'relative' }}>
                <table id="ab-test-table" className="tw-bg-[#DFE1E6] tw-w-screen">
                    <thead id="ab-test-table-header" className="tw-sticky tw-top-0 tw-bg-[#DFE1E6] tw-z-20">
                        <tr>
                            <th id="test-name-header" className="table-header-style tw-sticky tw-left-0 tw-z-20 tw-bg-[#DFE1E6]">Test name</th>
                            <th id="applets-header" className="table-header-style">Applets</th>
                            <th id="status-header" className="table-header-style">Status</th>
                            <th id="result-header" className="table-header-style">Result</th>
                            <th id="result-header" className="table-header-style">Models</th>
                            <th id="start-date-header" className="table-header-style">Start date</th>
                            <th id="end-date-header" className="table-header-style">End date</th>
                            <th id="updated-by-header" className="table-header-style">Updated by</th>
                            <th id="actions-header" className="table-header-style tw-w-[5%]"></th>
                        </tr>
                    </thead>
                    <tbody id="ab-test-table-body tw-z-10" className="tw-bg-white tw-divide-y tw-divide-[#F4F5F7]">
                        {abTests.map((row, rowIndex) => {
                            return <tr id={`ab-test-row-${rowIndex}`} key={row?.id}>
                                <td id={`test-name-${rowIndex}`} className={`table-item-style tw-align-top tw-truncate 
                                        ${(row?.status === "IN_PROGRESS" || row?.status === "COMPLETED") ? 'tw-text-primary tw-cursor-pointer' : ''} 
                                        tw-sticky tw-left-0 tw-z-10
                                        ${rowIndex % 2 === 0 ? 'tw-bg-[#F4F5F7]' : 'tw-bg-white'}`} onClick={() => {
                                        if (row?.status === "IN_PROGRESS" || row?.status === "COMPLETED") {
                                            navigateToResultPage(row?.name ?? '', row?.id ?? '')
                                        }
                                    }} >
                                    <div id={`test-name-text-${rowIndex}`} className='tw-truncate'>{row?.name ?? '-'}</div>
                                </td>
                                <TableCell id={`applets-${rowIndex}`} rowIndex={rowIndex}>
                                    <div id={`applet-name-${rowIndex}`} className="tw-text-[#090942] tw-text-sm tw-font-normal tw-leading-relaxed">
                                        {row?.applets?.[0]?.applet_name ?? ''}
                                    </div>
                                    <div id={`applet-id-${rowIndex}`} className="tw-text-[#6d788c] tw-text-xs tw-font-normal tw-leading-relaxed">
                                        {row?.applets?.[0]?.applet ?? ''}
                                    </div>
                                </TableCell>
                                <TableCell id={`status-${rowIndex}`} rowIndex={rowIndex}>
                                    {<CustomChip status={row?.status ?? ''} />}
                                </TableCell>
                                <TableCell id={`updated-by-${rowIndex}`} rowIndex={rowIndex}>
                                    {row?.test_result ?? '-'}
                                </TableCell>
                                <TableCell id={`result-${rowIndex}`} rowIndex={rowIndex} onClick={() => { }}>
                                    <div id={`result-container-${rowIndex}`} className='tw-flex tw-flex-col tw-w-full'>
                                        <ResultsList row={row} rowIndex={rowIndex} />
                                    </div>
                                </TableCell>
                                <TableCell id={`start-date-${rowIndex}`} rowIndex={rowIndex}>
                                    {convertToDateMonthYear(row?.started_at ?? '')}
                                </TableCell>
                                <TableCell id={`end-date-${rowIndex}`} rowIndex={rowIndex}>
                                    {convertToDateMonthYear(row?.ended_at ?? '')}
                                </TableCell>
                                <TableCell id={`updated-by-${rowIndex}`} rowIndex={rowIndex}>
                                    {row?.ownership?.username ?? ''}
                                </TableCell>
                                <TableCell id={`actions-${rowIndex}`} rowIndex={rowIndex}>
                                    <>
                                        <div
                                            id={`overflow-menu-trigger-${rowIndex}`}
                                            className="tw-cursor-pointer tw-py-2 tw-px-3 tw-rounded-lg hover:tw-bg-shades-of-grey-20"
                                            onClick={(e) => {
                                                if (rowIndex === showIndex) setShowIndex(null);
                                                else { handleOverflowClick(e, rowIndex); }
                                            }}
                                        >
                                            <OverFlowIcon />
                                        </div>

                                        {rowIndex === showIndex ? (
                                            <div
                                                id={`overflow-menu-${rowIndex}`}
                                                ref={menuRef}
                                                className="tw-fixed tw-rounded-[4px] tw-z-50  tw-bg-white tw-ring-1 tw-ring-[#EBECF0]"
                                                style={{ top: `${menuPosition.top}px`, left: `${menuPosition.left + 40}px` }}
                                            >
                                                <div id={`overflow-options-${rowIndex}`} className={`tw-origin-top-left tw-mr-8 tw-right-0 tw-absolute  tw-mx-3 tw-rounded-[4px] tw-z-50 tw-bg-white tw-ring-1 tw-ring-[#EBECF0]`}>
                                                    {OverflowOption(row, <EditIcon />, EDIT, onEdit, hasEditPermission(row))}
                                                    {OverflowOption(row, <DeleteIcon />, DELETE, onDelete, hasDeletePermission(row))}
                                                </div>
                                            </div>
                                        ) : null}
                                    </>
                                </TableCell>
                            </tr>
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default ABTable;
